import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Loader2 } from 'lucide-react';
import { newsService } from '../../../services/news.service';
import './HashtagPicker.css';

const SEARCH_DEBOUNCE_MS = 300;
const DROPDOWN_MAX_HEIGHT = 240;
/* Gap between the search field and the dropdown */
const DROPDOWN_OFFSET = 4;

/**
 * Attach an Inshorts hashtag to a short.
 *
 * One hashtag at a time: picking a second one replaces the first rather than
 * adding to it. `value` stays an array of `[{ label, title }]` - that is the
 * shape the API and the News document use either way - it just never holds
 * more than one entry while the editor is driving it. Shorts created before
 * this restriction can still carry several, and those are shown as they are.
 *
 * The label is the identity - it has to match the Inshorts list character for
 * character, so it is carried through exactly as the server returned it and
 * never retyped or re-cased. The title is only ever display.
 *
 * `language` picks which readable title the server resolves; the labels are
 * English on every tenant, so it does not change which hashtags are offered.
 *
 * `label` is the field's heading. It is rendered here rather than by the caller
 * because the selection sits on that same row - with one hashtag allowed, it
 * reads as part of the heading instead of a list growing under the field.
 */
const HashtagPicker = ({ value = [], onChange, language = 'en', label = null }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState(null);
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedLabels = new Set(value.map((h) => h.label));

  // The dropdown renders in a portal on document.body, so it needs viewport
  // coordinates. Going through the portal is what keeps it visible at all - the
  // edit layout nests this picker inside cards that clip their overflow, and an
  // in-flow absolute dropdown gets cut off by them.
  const positionDropdown = useCallback(() => {
    const field = searchRef.current;
    if (!field) return;

    const rect = field.getBoundingClientRect();
    const roomBelow = window.innerHeight - rect.bottom;
    // Flip above the field when it sits too close to the bottom of the screen
    const openUp = roomBelow < DROPDOWN_MAX_HEIGHT + DROPDOWN_OFFSET && rect.top > roomBelow;

    setDropdownPos({
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(120, (openUp ? rect.top : roomBelow) - DROPDOWN_OFFSET * 2),
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + DROPDOWN_OFFSET }
        : { top: rect.bottom + DROPDOWN_OFFSET }),
    });
  }, []);

  // Debounced search. The empty query is a real search too - it is what fills
  // the list when the editor first focuses the field.
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await newsService.getHashtags({ q: query, language });
        if (!cancelled) setResults(response.data?.hashtags || []);
      } catch (err) {
        console.error('Hashtag search failed:', err);
        if (!cancelled) setError('Could not load hashtags');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, language, isOpen]);

  // Keep the portaled dropdown pinned to the field. Scroll is captured so
  // ancestor scroll containers are caught too, not just the window.
  useEffect(() => {
    if (!isOpen) {
      setDropdownPos(null);
      return;
    }

    positionDropdown();
    window.addEventListener('scroll', positionDropdown, true);
    window.addEventListener('resize', positionDropdown);
    return () => {
      window.removeEventListener('scroll', positionDropdown, true);
      window.removeEventListener('resize', positionDropdown);
    };
  }, [isOpen, positionDropdown]);

  // Close the dropdown on an outside click, so it does not sit over the form.
  // The dropdown is outside containerRef now that it is portaled, so clicks
  // inside it have to be excluded explicitly.
  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e) => {
      const inField = containerRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inField && !inDropdown) setIsOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isOpen]);

  // Replaces whatever was selected instead of appending - a short carries one
  // hashtag. The dropdown closes too, so nothing invites a second pick.
  const selectHashtag = (hashtag) => {
    if (selectedLabels.has(hashtag.label) && value.length === 1) return;
    onChange([{ label: hashtag.label, title: hashtag.title || null }]);
    setQuery('');
    setIsOpen(false);
  };

  const removeHashtag = (label) => {
    onChange(value.filter((h) => h.label !== label));
  };

  return (
    <div className="hashtag-picker" ref={containerRef}>
      {(label || value.length > 0) && (
        <div className="hashtag-picker-header">
          {label && <label className="hashtag-picker-label">{label}</label>}
          {value.length > 0 && (
            <div className="hashtag-picker-selected">
              {value.map((hashtag) => (
                <span key={hashtag.label} className="hashtag-chip">
                  {/* The title is for reading; the label is what actually ships */}
                  <span className="hashtag-chip-title">{hashtag.title || hashtag.label}</span>
                  <button
                    type="button"
                    className="hashtag-chip-remove"
                    onClick={() => removeHashtag(hashtag.label)}
                    title={`Remove ${hashtag.label}`}
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="hashtag-picker-search" ref={searchRef}>
        <Search size={13} className="hashtag-picker-search-icon" />
        <input
          type="text"
          className="hashtag-picker-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={value.length > 0 ? 'Search to replace hashtag…' : 'Search hashtags…'}
        />
        {loading && <Loader2 size={13} className="animate-spin hashtag-picker-spinner" />}
      </div>

      {isOpen &&
        dropdownPos &&
        createPortal(
          <div className="hashtag-picker-dropdown" ref={dropdownRef} style={dropdownPos}>
            {error && <div className="hashtag-picker-message error">{error}</div>}

            {!error && !loading && results.length === 0 && (
              <div className="hashtag-picker-message">
                {query ? `No hashtag matches "${query}"` : 'No hashtags available'}
              </div>
            )}

            {!error &&
              results.map((hashtag) => {
                const isSelected = selectedLabels.has(hashtag.label);
                return (
                  <button
                    key={hashtag.label}
                    type="button"
                    className={`hashtag-picker-option${isSelected ? ' added' : ''}`}
                    onClick={() => selectHashtag(hashtag)}
                    // Only the lone selected hashtag is a no-op. On an older
                    // short carrying several, clicking one keeps it and drops
                    // the rest - which is a real change, so it stays enabled.
                    disabled={isSelected && value.length === 1}
                  >
                    <span className="hashtag-option-title">{hashtag.title || hashtag.label}</span>
                    {/* Show the raw label too when it differs, so the editor can
                        tell two similarly-titled hashtags apart */}
                    {hashtag.title && hashtag.title !== hashtag.label && (
                      <span className="hashtag-option-label">{hashtag.label}</span>
                    )}
                    {isSelected && <span className="hashtag-option-added">selected</span>}
                  </button>
                );
              })}
          </div>,
          document.body
        )}
    </div>
  );
};

export default HashtagPicker;
